import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { actions } from '../../../redux/modules/Actions';
import Form from '../../molecules/Form';

import Tag from '../../atoms/Tag';
import { SCREEN, stringDateToFormatString } from '../../../Common';
import Button from '../../atoms/Button';
import { SearchConditionState } from '../../../redux/reducers/SearchConditionReducer';
import ImportantProductsSummaryCard from '../../molecules/ImportantProductsSummaryCard';
import { CustomerInfoForList } from '../../../external/CustomerListApi';
import UpcomingActionSchedule from '../../molecules/UpcomingActionSchedule';
import Notice from '../../atoms/Notice';
import { getDisplayTemporaryCodesWithNegotiationName, getLeftAreaDisplayText, isUpdateDateOver60DaysFromToday } from './functions';
import { AppState } from '../../../redux/modules/store';
import { isCrewModule } from '../../../utils/Env';
import UpdateDate from '../../atoms/UpdateDate';

export interface CustomerListItemProps {
  customerInfo: CustomerInfoForList;
  updateGlobalStateByLocalState: () => void;
}

const CustomerListItem: React.FC<CustomerListItemProps> = ({ customerInfo, updateGlobalStateByLocalState }) => {
  const {
    customerId,
    customerNumber,
    customerFullName,
    ordcstmName,
    firstCreateUserName,
    hasKeep,
    hasCncgPrintNo,
    negotiationList,
    resurrectionFlg,
    createDate,
    latestUpdateDate,
    expirationDate,
    ownStoreFlg,
    familyStructureOtherCount,
    productInfo,
    upcomingActionSchedule,
    resurrectionDate,
  } = customerInfo;

  const history = useHistory();
  const reduxDispatch = useDispatch();

  /**
   * 現在日付(お客さま情報一覧APIのレスポンスに含まれるjobDateTime)を取得します。
   */
  const today = useSelector((state: AppState) => state.loadingStatus.fetchCustomerInfoList.result?.jobDateTime) || '';

  /**
   * 自店舗フラグが「1」の場合は自店舗の顧客情報であるためtrue、それ以外はfalseとする
   */
  const isOwnStoreCustomer = ownStoreFlg === '1';

  /**
   * 復活予定フラグが「1」の場合は復活予定案件であるためtrue、それ以外はfalseとする
   */
  const isResurrectionCustomer = resurrectionFlg === '1';

  /**
   * お客さま情報への遷移ボタンの表示有無
   * クルーモジュール且つ、自店舗の顧客情報である場合にのみ表示するためtrue、それ以外はfalseとする
   */
  const showToCustomerInfoButton = isCrewModule() && isOwnStoreCustomer;

  /**
   * お客さま氏名を表示するかどうかのフラグ
   * クルーモジュール且つ、自店舗の顧客情報である且つ、復活予定でない場合にのみ表示するためtrue、それ以外はfalseとする
   */
  const showCustomerName = isCrewModule() && !isResurrectionCustomer && isOwnStoreCustomer;

  /**
   * 店舗名表示有無
   * クルーモジュールではない場合は、常に表示するためtrue。
   * クルーモジュールの場合は、自店舗の顧客情報でない場合にのみ表示するため、falseとする
   * また、このフラグは"お客さま氏名"を表示するか、"お客さま番号"を表示するかの条件にも使用する
   */
  const showStoreName = !isCrewModule() ? true : !isOwnStoreCustomer;

  /**
   * お客さま氏名表示用定数
   * ブランクの場合は'-'を表示する
   */
  const displayCustomerNameText = getLeftAreaDisplayText(customerFullName || '-');

  /**
   * お客さま番号表示用定数
   * カッコ付きで表示する
   */
  const displayCustomerNumberText = getLeftAreaDisplayText(`(${customerNumber})`);

  /**
   * 初回登録者表示用定数
   */
  // nullの可能性がある項目は、APIのレスポンスでブランクではなく'-'を返すように修正する
  const displayCreateUserNameText = getLeftAreaDisplayText(firstCreateUserName || '-');

  /**
   * 他社携帯利用中人数表示用定数
   */
  const displayOtherUseCountText = `他社携帯利用中 ${familyStructureOtherCount || 0}人`;

  /**
   * 店舗名表示用定数
   */
  // nullの可能性がある項目は、APIのレスポンスでブランクではなく'-'を返すように修正する
  const displayStoreNameText = getLeftAreaDisplayText(ordcstmName || '-', 8);

  /**
   * 商談一覧画面遷移ボタンラベル
   */
  const negotiationListButtonLabel = `対象商談 ${negotiationList ? negotiationList.length : 0}件`;

  /**
   * 復活予定案件の復活日付表示用定数
   */
  const formattedResurrectionDate = resurrectionDate && stringDateToFormatString(resurrectionDate, 'yyyy-MM-dd', 'yyyy/MM/dd');

  /**
   * 復活予定案件の表示用定数
   */
  const displayResurrectionTitleLabel = `復活予定案件（${formattedResurrectionDate}から、閲覧・編集ができるようになります）`;

  /**
   * 商談情報表示用定数
   * - 取り置き、料金シミュレーター提案、臨時コードの有無を表示する
   * - 臨時コードは「商談名 : tempCd1,tempCd2,tempCd3,tempCd4」の形式で表示する
   */
  const displayNegotiationInfoList: string[] = negotiationList
    ? [
        `取り置き : ${hasKeep ? 'あり' : 'なし'}`,
        `料金シミュレーター提案 : ${hasCncgPrintNo ? 'あり' : 'なし'}`,
        `臨時コード : ${getDisplayTemporaryCodesWithNegotiationName(negotiationList).join(',')}`,
      ]
    : [];

  const handleClickCustomerInfoButton = () => {
    // 遷移先から戻る可能性があるため、遷移前にグローバルステートを更新する
    updateGlobalStateByLocalState();
    history.push(`${SCREEN.CUSTOMER_DETAIL.URL}/${customerId}`);
  };

  const handleClickSummaryButton = () => {
    // 遷移先から戻る可能性があるため、遷移前にグローバルステートを更新する
    updateGlobalStateByLocalState();
    history.push(`${SCREEN.SUMMARY_PREVIEW.URL}/${customerId}`);
  };

  const handleClickNegotiationButton = () => {
    // 商談一覧へ遷移する場合は、"商談一覧の"絞り込み条件にお客さま番号を設定する
    const searchCondition: SearchConditionState = [
      {
        searchConditionName: 'customerNumberFilterKeyword',
        specifiedCondition: [customerNumber],
        isUsedInFilter: true,
      },
    ];
    reduxDispatch(actions.updateSearchCondition(searchCondition));
    // 商談一覧へ遷移する場合は、locationHistoryのデータをリセットする（商談一覧画面では、locationHistoryのデータがある場合は、遷移元に応じた遷移前の画面へ戻る挙動となるため）
    reduxDispatch(actions.locationHistory.resetData());
    history.push(SCREEN.NEGOTIATION_SEARCH_RESULT.URL);
  };

  /**
   * クルーモジュール且つ、自店舗の顧客情報でない場合は背景色を薄水色とします
   * colorの詳細はstyleのlessを参照
   */
  const cardClassName = isCrewModule() && !isOwnStoreCustomer ? 'customer-list-readonly' : '';

  /**
   * 60日以上更新のない顧客情報の更新日付は赤色で表示します
   * colorの詳細はstyleのlessを参照
   */
  const updateDateClassName = isUpdateDateOver60DaysFromToday(latestUpdateDate, today) ? 'u-text-danger' : '';

  return (
    <li key={customerId} className={classNames('customer-list-card', cardClassName)}>
      {/* 復活予定案件である場合、復活日付を表示する} */}
      {isResurrectionCustomer && formattedResurrectionDate && (
        <div className='u-flex u-flex-column u-h-full u-ph16 u-pt16'>
          <Notice
            icon='info'
            titleLabel={displayResurrectionTitleLabel}
            isInline
            type='info'
            id='notice'
            data-testid='resurrection-date-message'
            data-testid-e2e='resurrection-date-message'
          />
        </div>
      )}
      <div className='customer-list-card__content u-pb0'>
        <div className='customer-list-card__content-col customer-list-col-left u-min-ws'>
          {/* カード内 左(お客さま氏名、お客さま番号、初回登録者、店舗名、退社携帯利用中人数)の表示領域 */}
          <div className={classNames('customer-list-card__content-col', 'customer-list-col-left', 'customer-list-card__left-content')}>
            {/* お客さま氏名 or お客さま番号 */}
            {showCustomerName ? (
              <Form.Fileld label='お客さま氏名' className={classNames('u-mb16', 'uc-button--max-wml')}>
                <div data-testid='searchResultItem-customerFullName' data-testid-e2e='searchResultItem-customerFullName'>
                  {displayCustomerNameText}
                </div>
                <div
                  data-testid='searchResultItem-customerNumber'
                  data-testid-e2e='searchResultItem-customerNumber'
                  className={classNames('uc-form-field__label', 'u-text-disabled')}>
                  {displayCustomerNumberText}
                </div>
              </Form.Fileld>
            ) : (
              <Form.Fileld label='お客さま番号' className={classNames('u-mb16', 'uc-button--max-wml')}>
                <div data-testid='searchResultItem-customerNumber' data-testid-e2e='searchResultItem-customerNumber'>
                  {customerNumber}
                </div>
              </Form.Fileld>
            )}

            {/* 初回登録者 */}
            <Form.Fileld label='初回登録者' className={classNames('u-mb16')}>
              <div data-testid='searchResultItem-closingSalesmanName' data-testid-e2e='searchResultItem-closingSalesmanName'>
                {displayCreateUserNameText}
              </div>
            </Form.Fileld>

            {/* 店舗名 */}
            {/* クルーモジュールでない場合、または、自店舗以外の情報の場合にのみ表示します */}
            {showStoreName && (
              <Form.Fileld label='店舗名' className={classNames('u-mb16')}>
                <div data-testid='searchResultItem-ordcstmName' data-testid-e2e='searchResultItem-ordcstmName'>
                  {displayStoreNameText}
                </div>
              </Form.Fileld>
            )}
            {/* 他社携帯利用中人数 */}
            <Form.Fileld label='' className={classNames('u-mb16')}>
              <Tag className={classNames('u-text-normal')} label={displayOtherUseCountText} />
            </Form.Fileld>
          </div>
        </div>

        <div className={classNames('customer-list-card__content-col', 'u-flex-1')}>
          <div className={classNames('customer-list-card__products')}>
            {/* 商談中の重要商品の表示 */}
            <ImportantProductsSummaryCard
              smartphoneChangeCount={productInfo.smartphoneChange}
              smartphoneNewCount={productInfo.smartphoneNew}
              smartphoneMnpCount={productInfo.smartphoneMnp}
              smartphoneTransferCount={productInfo.smartphoneTransfer}
              auroraContractCount={productInfo.aurora}
              sbHikariContractCount={productInfo.sbHikari}
              sbAirContractCount={productInfo.sbAir}
            />
            {/* 直近のアクション予定の表示 */}
            <Form.Fileld label='直近のアクション予定'>
              <div data-testid-e2e='searchResultItem-latestActionPlan'>
                {upcomingActionSchedule ? (
                  <UpcomingActionSchedule {...upcomingActionSchedule} />
                ) : (
                  <p className='u-text-disabled u-text-m'>アクション予定がありません</p>
                )}
              </div>
            </Form.Fileld>
          </div>
        </div>
        {/* ボタン表示領域 */}
        <div className={classNames('customer-list-card__content-col', 'customer-list-card__buttons')}>
          {/* お客さま情報への遷移ボタン */}
          <div>
            {/* クルーモジュール且つ、自店舗の場合にのみ表示します */}
            {showToCustomerInfoButton && (
              <Button
                width='full'
                className='u-mb8'
                label='お客さま情報'
                onClick={handleClickCustomerInfoButton}
                disabled={isResurrectionCustomer} // 復活予定の顧客は遷移不可とする
                data-testid='toCustomer'
              />
            )}
          </div>
          {/* サマリーへの遷移ボタン */}
          <div>
            <Button
              width='full'
              className={classNames('u-mb8')}
              label='サマリー'
              onClick={handleClickSummaryButton}
              disabled={isResurrectionCustomer} // 復活予定の顧客は遷移不可とする
              data-testid='to-summary-preview'
            />
          </div>
          {/* 商談一覧への遷移ボタン */}
          <div>
            <Button
              width='full'
              type='outlined'
              className={classNames('u-mb8')}
              label={negotiationListButtonLabel}
              onClick={handleClickNegotiationButton}
              data-testid='to-search-result'
            />
          </div>
        </div>
      </div>

      <div className={classNames('u-pr16', 'u-pl16', 'u-pb8')}>
        <div className={classNames('u-text-right', 'u-pb8')}>
          <span data-testid='customer-create-date' data-testid-e2e='customer-create-date' className='u-mr16'>
            <UpdateDate size='small' updateDate={stringDateToFormatString(createDate, 'yyyy-MM-dd', 'yyyy/MM/dd')} labelType='create' />
          </span>
          <span
            data-testid='customer-update-date'
            data-testid-e2e='customer-update-date'
            className={classNames(expirationDate ? 'u-mr16' : '', updateDateClassName)}>
            <UpdateDate
              size='small'
              updateDate={stringDateToFormatString(latestUpdateDate, 'yyyy-MM-dd', 'yyyy/MM/dd')}
              labelType='update'
              isDanger={isUpdateDateOver60DaysFromToday(latestUpdateDate, today)}
            />
          </span>
          {expirationDate && (
            <span data-testid='customer-limit-date' data-testid-e2e='customer-limit-date'>
              <UpdateDate
                size='small'
                updateDate={stringDateToFormatString(expirationDate, 'yyyy-MM-dd', 'yyyy/MM/dd')}
                labelType='deadline'
              />
            </span>
          )}
        </div>
        {/* 商談情報が存在しない場合は非表示 */}
        {negotiationList && negotiationList.length > 0 && (
          <div className={classNames('customer-list-table__detail-panel', 'u-pr0', 'u-pl0', cardClassName)}>
            <div
              className={classNames('uc-panel', 'uc-panel--gray', 'u-pv8', 'customer-list-table__detail-panel__content')}
              data-testid='customer-list-negotiation-detail-info'
              data-testid-e2e='customer-list-negotiation-detail-info'>
              {displayNegotiationInfoList.join(' / ')}
            </div>
          </div>
        )}
      </div>
    </li>
  );
};

export default CustomerListItem;
